// Minimal IndexedDB save store (replay-based saves, DESIGN.md §12.4).
// Saves are {seed, mapSize, commandLog} + tick — loading re-runs the replay.

import type { Replay } from '../shared/protocol';

export interface SaveEntry {
  name: string;
  savedAt: number;
  tick: number;
  replay: Replay;
}

const DB_NAME = 'portage';
const STORE = 'saves';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
  });
}

export async function saveGame(entry: SaveEntry): Promise<void> {
  await tx('readwrite', (s) => s.put(entry, entry.name));
}

export async function loadGame(name: string): Promise<SaveEntry | undefined> {
  return tx<SaveEntry | undefined>('readonly', (s) => s.get(name) as IDBRequest<SaveEntry | undefined>);
}

export async function listSaves(): Promise<string[]> {
  const keys = await tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
  return keys.map(String).sort();
}
